import { DEFAULT_AI_SEARCH_ACCURACY } from 'constants/defaultAiSearchAccuracy';
import { API_CLIENT_NAMES } from 'services/API';
import strings from 'strings';
import { FilterType } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { Groupers, LabelRenderers, OptionRenderers } from 'UI/utils/renderers';

import { ENDPOINTS } from '../../ResumeSearch.constants';

const FILTER_NAMES = strings.resumeSearch.filters.names;

export const FILTER_CONFIG = [
  {
    title: FILTER_NAMES.AISearch,
    type: FilterType.Search,
    name: 'ai-search',
    fieldName: 'summary',
    placeholder: strings.resumeSearch.filters.aiSmartSearchPlaceholder,
    formatValue: value => {
      if (!value || !value.aiSummary) return null;
      return {
        item: {
          value: {
            smart: {
              accuracy: value.accuracy,
              text: value.aiSummary
            }
          }
        },
        priority: 'must_have'
      };
    },
    getValue: value => {
      if (!value || !value?.item?.value.smart?.text) return null;
      return {
        accuracy: value?.item?.value.smart?.accuracy || DEFAULT_AI_SEARCH_ACCURACY,
        aiSummary: value?.item?.value.smart?.text
      };
    }
  },
  {
    title: FILTER_NAMES.industry,
    type: FilterType.AdvancedSearch,
    name: 'industry',
    endpoint: ENDPOINTS.Industries,
    typeahead: false,
    typeaheadLimit: 50
  },
  {
    title: FILTER_NAMES.specialty,
    type: FilterType.AdvancedSearch,
    name: 'specialty',
    fieldName: 'basics.specialty.title',
    endpoint: Endpoints.Specialties,
    apiClient: API_CLIENT_NAMES.Primary,
    renderOption: OptionRenderers.title,
    getOptionLabel: LabelRenderers.specialty,
    groupBy: Groupers.byIndustry,
    typeaheadParams: { queryKey: 'keyword' },
    formatSelectedItems: selectedItems =>
      selectedItems.map(item => ({ ...item, item: { value: item.item.title, ...item.item } })),
    typeahead: true
  },
  {
    title: FILTER_NAMES.subspecialty,
    type: FilterType.AdvancedSearch,
    name: 'subspecialty',
    fieldName: 'basics.subspecialty.title',
    endpoint: Endpoints.SubspecialtiesSearch,
    apiClient: API_CLIENT_NAMES.Primary,
    renderOption: OptionRenderers.subspecialty,
    getOptionLabel: LabelRenderers.subspecialty,
    formatSelectedItems: selectedItems =>
      selectedItems.map(item => ({ ...item, item: { value: item.item.title, ...item.item } })),
    typeaheadParams: { queryKey: 'keyword' },
    typeahead: true
  },
  {
    title: FILTER_NAMES.skills,
    type: FilterType.AdvancedSearch,
    name: 'skills',
    endpoint: ENDPOINTS.Skills,
    typeahead: true
  },
  {
    title: FILTER_NAMES.activities,
    type: FilterType.AdvancedSearch,
    name: 'activities',
    fieldName: 'lastActivityByType.type',
    preferencesFieldName: 'lastActivityByType.date',
    preferencesEndpoint: Endpoints.ActivityTimeFilters,
    endpoint: `${Endpoints.ActivityTypes}?showAll=true`,
    apiClient: API_CLIENT_NAMES.Primary,
    renderOption: OptionRenderers.title,
    getOptionLabel: LabelRenderers.title,
    formatSelectedItems: selectedItems =>
      selectedItems.map(item => ({ ...item, item: { value: item.item.title, ...item.item } }))
  },
  {
    title: FILTER_NAMES.languages,
    type: FilterType.AdvancedSearch,
    name: 'languages',
    endpoint: ENDPOINTS.Languages,
    typeahead: true,
    typeaheadLimit: 20
  },
  {
    title: FILTER_NAMES.companies,
    type: FilterType.AdvancedSearch,
    name: 'companies',
    endpoint: ENDPOINTS.ProfessionalExperiencesCompanies,
    typeahead: true
  },
  {
    title: FILTER_NAMES.positions,
    type: FilterType.AdvancedSearch,
    name: 'positions',
    endpoint: ENDPOINTS.ProfessionalExperiencesTitles,
    typeahead: true
  },
  {
    title: FILTER_NAMES.professionalAssociations,
    type: FilterType.AdvancedSearch,
    name: 'professionalAssociationsIssuingOrganizations',
    endpoint: ENDPOINTS.ProfessionalAssociationsIssuingOrganizations,
    typeahead: true
  },
  {
    title: FILTER_NAMES.certifications,
    type: FilterType.AdvancedSearch,
    name: 'certifications',
    endpoint: ENDPOINTS.CertificationsDescriptions,
    typeahead: true
  },
  {
    title: FILTER_NAMES.certifyingOrganizations,
    type: FilterType.AdvancedSearch,
    name: 'certifyingOrganizations',
    endpoint: ENDPOINTS.CertificationsIssuingOrganizations,
    typeahead: true
  },
  {
    title: FILTER_NAMES.degrees,
    type: FilterType.AdvancedSearch,
    name: 'degrees',
    endpoint: ENDPOINTS.EducationsDegrees,
    typeahead: true
  },
  {
    title: FILTER_NAMES.educationsIssuingOrganizations,
    type: FilterType.AdvancedSearch,
    name: 'educationsIssuingOrganizations',
    endpoint: ENDPOINTS.EducationsIssuingOrganizations,
    typeahead: true
  },
  {
    title: FILTER_NAMES.majors,
    type: FilterType.AdvancedSearch,
    name: 'majors',
    endpoint: ENDPOINTS.EducationsMajors,
    typeahead: true
  },
  {
    title: FILTER_NAMES.states,
    type: FilterType.AdvancedSearch,
    name: 'states',
    endpoint: ENDPOINTS.LocationsStates,
    typeahead: true
  },
  {
    title: FILTER_NAMES.cities,
    type: FilterType.AdvancedSearch,
    name: 'cities',
    endpoint: ENDPOINTS.LocationsCities,
    typeahead: true
  },
  {
    title: FILTER_NAMES.statuses,
    type: FilterType.AdvancedSearch,
    name: 'statuses',
    endpoint: ENDPOINTS.Statuses,
    typeahead: false
  },
  {
    title: FILTER_NAMES.types,
    type: FilterType.AdvancedSearch,
    name: 'types',
    endpoint: ENDPOINTS.Types,
    typeahead: false
  },
  {
    title: FILTER_NAMES.workPreferences,
    type: FilterType.AdvancedSearch,
    name: 'workPreferences',
    endpoint: ENDPOINTS.WorkPreferences,
    typeahead: false
  },
  {
    title: FILTER_NAMES.timesToStart,
    type: FilterType.AdvancedSearch,
    name: 'timesToStart',
    endpoint: ENDPOINTS.TimesToStart,
    typeahead: false
  },
  {
    title: FILTER_NAMES.reasonsForLooking,
    type: FilterType.AdvancedSearch,
    name: 'reasonsForLooking',
    endpoint: ENDPOINTS.ReasonsForLooking,
    typeahead: false
  },
  {
    title: FILTER_NAMES.openToRelocation,
    type: FilterType.AdvancedSearch,
    name: 'openToRelocation',
    endpoint: ENDPOINTS.OpenToRelocation,
    typeahead: false
  }
];

export const FILTER_DEFAULT_VALUES = FILTER_CONFIG.reduce((acc, config) => {
  acc[config.name] = {
    fieldName: config.fieldName || null,
    preferencesFieldName: config.preferencesFieldName || null,
    value: null
  };
  return acc;
}, {});

export const PRIORITY_MAP = {
  must_have: 'MUST',
  can_have: 'SHOULD',
  doesnt_have: 'NOT'
};
